import { addDays } from './iso-date';

export type BookedRange = { checkin: string; checkout: string };

// Nights occupied by the given bookings — half-open [checkin, checkout),
// so a booking's checkout day is NOT a booked night.
export function expandBookedNights(ranges: BookedRange[]): Set<string> {
  const nights = new Set<string>();
  for (const r of ranges) {
    let cursor = r.checkin;
    while (cursor < r.checkout) {
      nights.add(cursor);
      cursor = addDays(cursor, 1);
    }
  }
  return nights;
}

export function nextCheckinAfter(ranges: BookedRange[], startIso: string): string | null {
  let next: string | null = null;
  for (const r of ranges) {
    if (r.checkin > startIso && (next === null || r.checkin < next)) {
      next = r.checkin;
    }
  }
  return next;
}

// Role-aware disabled check for the range datepicker.
// - Fresh state (no anchor) → the click means CHECK-IN: every booked night
//   is disabled.
// - Anchored (a start is being picked, or a completed range exists whose
//   checkin is passed as completedCheckinIso) → the closest checkin after
//   the anchor is selectable as a CHECK-OUT (the night before it is free by
//   construction of "closest"), unless that day is also an occupied night
//   of an earlier booking (back-to-back), in which case it stays disabled.
//   For a completed range the click lands as a new range start — the widget
//   intercepts it and moves the checkout instead.
export function isDisabledForSelection(
  iso: string,
  ranges: BookedRange[],
  bookedNights: Set<string>,
  pendingStartIso: string | null,
  completedCheckinIso: string | null = null,
): boolean {
  if (!bookedNights.has(iso)) return false;
  const anchor = pendingStartIso ?? completedCheckinIso;
  if (!anchor) return true;
  if (iso !== nextCheckinAfter(ranges, anchor)) return true;
  // Checkout-only day: allowed iff the preceding night is free.
  return bookedNights.has(addDays(iso, -1));
}
